// Keyboard containment for a modal, shared by every dialog that has one.
//
// 🚩 Extracted so there is ONE answer to "what can Tab land on" and "where does it go next". There
// were two copies and they disagreed — `ui-dialogs.tsx` had both of the faults below while the
// image-optimize modal had neither, which is exactly how a trap silently stops trapping.

/**
 * What Tab is allowed to land on inside a modal.
 *
 * 🔴 `:not([disabled])` matters and was missing. A modal that disables its controls while it works
 * (the image-optimize one disables all five) would otherwise have `.focus()` called on elements
 * that cannot accept it: Tab appears dead, and focus leaks to the page behind. With this, an
 * all-disabled panel correctly matches nothing and the caller holds focus on the panel itself.
 */
export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Where Tab should go, given how many focusable items there are and where focus is now.
 * Returns the index to focus, or -1 for "leave it alone — the browser's own Tab is correct".
 *
 * 🔴 `current === -1` (focus is outside the panel) must pull focus BACK. The old `idx <= 0` form
 * handled that for shift-Tab by accident and not at all for forward Tab, so focus that had escaped
 * — or had never been inside — kept walking into the page behind the dialog.
 */
export function nextFocusIndex(count: number, current: number, shiftKey: boolean): number {
  if (count <= 0) return -1;
  if (current === -1) return shiftKey ? count - 1 : 0;
  if (shiftKey && current === 0) return count - 1;         // wrap backwards off the front
  if (!shiftKey && current === count - 1) return 0;        // wrap forwards off the end
  return -1;                                               // mid-list: the browser handles it
}

/** The focusable children of a panel, in document order. */
export function focusableIn(panel: HTMLElement | null): HTMLElement[] {
  return Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []);
}

/**
 * The shared Tab handler. Returns true if it handled the key (and called preventDefault).
 * Escape is deliberately NOT handled here — each dialog means something different by it.
 */
export function handleTrapKey(e: KeyboardEvent, panel: HTMLElement | null): boolean {
  if (e.key !== 'Tab') return false;
  const list = focusableIn(panel);
  if (!list.length) { e.preventDefault(); panel?.focus(); return true; }
  const target = nextFocusIndex(list.length, list.indexOf(document.activeElement as HTMLElement), e.shiftKey);
  if (target === -1) return false;
  e.preventDefault();
  list[target].focus();
  return true;
}
